import {
  useGetFormDetail2,
  useGetFormalityRequestDetail,
  useGetRevisions,
  type Revision,
} from "@/queries/formality-request";
import type {
  FormalityRequestType,
  FormDetailResponse,
} from "@/types/entities/formality-request";
import { Heading, Icon, Badge } from "@elaniintc/goes-design-system";
import { useParams } from "react-router";
import { useInfo } from "../../hooks/use-info";
import { useEffect, useMemo } from "react";
import { cn } from "@/utils/theme";
import { getInternalDeadlineFromFormData } from "@/utils/procedures-ui";
import { RevisionSelector } from "./revision-selector";
import { useGetMe } from "@/queries/me";
import { getCorrections } from "../utils/utils";
import { FormCorrectionEdit } from "./form-correction/components/form-correction-edit";
import { FormCorrectionRejectModal } from "./form-correction/components/modal-reject";
import { FormCorrectionApproveModal } from "./form-correction/components/modal-approve";
import { FormCorrectionReadOnlyView } from "./form-correction/components/read-only-view";
import {
  CorrectionFormProvider,
  useCorrectionForm,
} from "./form-correction/context";
import { REVISION_RELATIONSHIPS } from "./form-correction/const";
import { buildVisibleSteps } from "./form-correction/utils/steps";
import { usePendingVoBo } from "./form-correction/hooks/use-pending-vobo";
import { useLeaderApprovalActions } from "./form-correction/hooks/use-leader-approval-actions";
import { useSubmitCorrections } from "./form-correction/hooks/use-submit-corrections";

interface FormCorrectionContentProps {
  formalityRequest?: FormalityRequestType;
  form?: FormDetailResponse;
  revisionsRaw?: Revision[];
  procedureId: string;
  isFetching: boolean;
  isLoadingRevisions: boolean;
  isFetchingRevisions: boolean;
}

const FormCorrectionContent = ({
  formalityRequest,
  form: data,
  revisionsRaw = [],
  procedureId,
  isFetching,
  isLoadingRevisions,
  isFetchingRevisions,
}: FormCorrectionContentProps) => {
  const {
    setRejectComments,
    selectedRevisionId,
    setRevisionId,
    selectedGroupId,
    setGroupId,
    revisionId,
    isReadOnlyView,
    revisions,
    isUserLeaderOfSelectedGroup,
    setCorrections,
    setLockedApprovedFields,
    setNeedsCorrectionCount,
    setNonPreventableFields,
    corrections,
    needsCorrectionCount,
  } = useCorrectionForm();
  const { isCancelled } = useInfo();
  const { setShouldShowInfo } = useInfo();

  const { pendingVoBoMap, pendingVoBoCount } = usePendingVoBo(revisionsRaw);

  const { setFormDeadline } = useInfo();

  useEffect(() => {
    if (data) {
      setShouldShowInfo(false);
      const { corr, lockedFields, count, nonPreventableFields } =
        getCorrections(data, revisionId, selectedGroupId);
      setCorrections(corr);
      setLockedApprovedFields(lockedFields);
      setNeedsCorrectionCount(count);
      setNonPreventableFields(nonPreventableFields);
      const exp = getInternalDeadlineFromFormData(data);
      setFormDeadline(exp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, revisionId, selectedGroupId, setShouldShowInfo]);

  const canSubmitCorrections = corrections.size === needsCorrectionCount;

  const {
    leaderModalState,
    setLeaderModalState,
    isLoadingLeaderApproval,
    handleLeaderReject,
    handleLeaderApprove,
  } = useLeaderApprovalActions(procedureId);

  const {
    modalState,
    setModalState,
    isLoadingUpdateRevision,
    onSubmitCorrections,
    handleSubmitWithGeneralComment,
  } = useSubmitCorrections({ data, formalityRequest, procedureId });

  useEffect(() => {
    return () => {
      setCorrections(new Map());
    };
  }, [setCorrections]);

  const handleRevisionSelect = (revisionId: string, groupId?: string) => {
    setRevisionId(revisionId);
    setGroupId(groupId || null);
  };

  const shouldDisabledDropdown = useMemo(() => {
    //should be disabled if there are no revisions or if there is only one revision without groups
    if (revisions.length === 0) return true;
    if (revisions.length === 1) {
      const rev = revisions[0];
      return !rev.groups || rev.groups.length === 1 || rev.groups.length === 0;
    }
    return false;
  }, [revisions]);

  const visibleStepsWithIndices = useMemo(
    () => buildVisibleSteps(data),
    [data],
  );

  if (isFetching || !data || isLoadingRevisions) {
    return (
      <div>
        <Icon
          name="spinner-third"
          type="fas"
          className="text-on-disabled animate-spin"
        />
      </div>
    );
  }

  const renderFormContent = () => {
    if (!selectedRevisionId) {
      return (
        // Mostrar mensaje para seleccionar una revisión
        <div className="flex flex-col items-center justify-center gap-4 px-4 pt-20">
          <Icon name="file-edit" type="fas" className="text-white text-5xl" />
          <div className="text-center">
            <Heading variant="h6" className="text-neutral-loud">
              Selecciona una revisión
            </Heading>
            <p className="text-body text-sm mt-2">
              Utiliza el selector de arriba para elegir la revisión que deseas
              revisar
            </p>
          </div>
        </div>
      );
    }
    if (isReadOnlyView) {
      return (
        <FormCorrectionReadOnlyView
          isFetchingRevisions={isFetchingRevisions}
          visibleStepsWithIndices={visibleStepsWithIndices}
          isLoadingLeaderApproval={isLoadingLeaderApproval}
          setLeaderModalState={setLeaderModalState}
        />
      );
    }
    return (
      <FormCorrectionEdit
        isFetchingRevisions={isFetchingRevisions}
        visibleStepsWithIndices={visibleStepsWithIndices}
        canSubmitCorrections={canSubmitCorrections}
        isCancelled={isCancelled}
        modalState={modalState}
        onSubmitCorrections={onSubmitCorrections}
        isLoadingUpdateRevision={isLoadingUpdateRevision}
        handleSubmitWithGeneralComment={handleSubmitWithGeneralComment}
        onCloseModal={() => setModalState("idle")}
      />
    );
  };

  return (
    <>
      {/* Header with Revision Selector */}
      <div
        className={cn(
          "flex items-center gap-3 px-4 pb-4",
          !shouldDisabledDropdown && "border-b border-neutral-quiet",
        )}
      >
        <RevisionSelector
          revisions={revisions}
          selectedRevisionId={selectedRevisionId || undefined}
          selectedGroupId={selectedGroupId || undefined}
          onSelect={handleRevisionSelect}
          pendingVoBo={pendingVoBoMap}
          shouldDisabledDropdown={shouldDisabledDropdown}
        />
        {pendingVoBoCount > 0 && isUserLeaderOfSelectedGroup && (
          <Badge
            variant="warning"
            className="flex items-center gap-1.5 px-3 py-1 shrink-0"
          >
            <Icon type="fas" name="arrow-left" className="text-xs" />
            Aprobaciones pendientes: {pendingVoBoCount}
          </Badge>
        )}
      </div>

      {/* Form content */}
      {renderFormContent()}

      {/* Modal de Rechazo del Líder */}
      <FormCorrectionRejectModal
        isOpen={leaderModalState === "reject"}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setLeaderModalState("idle");
            setRejectComments("");
          }
        }}
        onReject={handleLeaderReject}
        isLoading={isLoadingLeaderApproval}
      />

      {/* Modal de Aprobación del Líder */}
      <FormCorrectionApproveModal
        isOpen={leaderModalState === "approve"}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setLeaderModalState("idle");
          }
        }}
        onApprove={handleLeaderApprove}
        isLoading={isLoadingLeaderApproval}
        applicantName={`${formalityRequest?.applicant?.first_name || ""} ${formalityRequest?.applicant?.last_name || ""}`.trim()}
        formName={data?.name || formalityRequest?.name || ""}
      />
    </>
  );
};

const FormCorrection = () => {
  const params = useParams<{ formId: string; procedureId: string }>();
  const formId = params.formId ?? "";
  const procedureId = params.procedureId ?? "";

  const { data, isPending, isLoading } = useGetFormDetail2(formId, procedureId);

  const { data: formalityRequest } = useGetFormalityRequestDetail(procedureId);

  // Get user profile to filter groups
  const { data: userProfile } = useGetMe();

  // Fetch revisions for the formality request using the correct endpoint
  const {
    data: revisionsRaw = [],
    isLoading: isLoadingRevisions,
    isFetching: isFetchingRevisions,
  } = useGetRevisions({
    relationships: REVISION_RELATIONSHIPS,
    filters: {
      formality_request_id: procedureId,
    },
    enabled: !!procedureId,
  });

  return (
    <CorrectionFormProvider
      formalityRequest={formalityRequest}
      form={data}
      revisionsRaw={revisionsRaw}
      userProfile={userProfile}
    >
      <FormCorrectionContent
        procedureId={procedureId}
        isFetching={isPending || isLoading}
        form={data}
        formalityRequest={formalityRequest}
        revisionsRaw={revisionsRaw}
        isLoadingRevisions={isLoadingRevisions}
        isFetchingRevisions={isFetchingRevisions}
      />
    </CorrectionFormProvider>
  );
};

export default FormCorrection;
